import { useState, useEffect } from 'react';
import { CheckCircle2, Eye, RotateCcw, Plus, X } from 'lucide-react';
import { FOREST_GREEN, MID_GRAY, ERROR, WARNING, BORDER, SHADOWS, WHITE } from '@/styles/tokens';

function verdictColor(verdict: 'pass' | 'warn' | 'fail'): string {
  switch (verdict) {
    case 'pass': return FOREST_GREEN.DEFAULT;
    case 'warn': return WARNING.DEFAULT;
    case 'fail': return ERROR.DEFAULT;
  }
}

function camelToTitle(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

function formatTime(ms: number): string {
  const totalSec = Math.round(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

interface PlanCompletionCardProps {
  artifactCounts: Record<string, number>;
  overallAuditScore?: number;
  totalLLMCalls: number;
  executionTimeMs: number;
  onStartNewPlan: () => void;
  onViewDetails: () => void;
  onUndoAll?: () => void;
  onClose?: () => void;
}

export function PlanCompletionCard({
  artifactCounts,
  overallAuditScore,
  totalLLMCalls,
  executionTimeMs,
  onStartNewPlan,
  onViewDetails,
  onUndoAll,
  onClose,
}: PlanCompletionCardProps) {
  const [showUndoConfirm, setShowUndoConfirm] = useState(false);
  const totalArtifacts = Object.values(artifactCounts).reduce((a, b) => a + b, 0);

  // Browser notification when tab is hidden
  useEffect(() => {
    if (!document.hidden) return;

    if (Notification.permission === 'granted') {
      new Notification('Plan Complete — TerraMatrix', {
        body: `Created ${totalArtifacts} artifacts in ${formatTime(executionTimeMs)}`,
      });
    }

    // Flash document title
    const originalTitle = document.title;
    const flashTitle = '\u2705 Plan Complete — TerraMatrix';
    let isFlashed = false;
    const interval = setInterval(() => {
      document.title = isFlashed ? originalTitle : flashTitle;
      isFlashed = !isFlashed;
    }, 1000);

    const handleFocus = () => {
      clearInterval(interval);
      document.title = originalTitle;
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(interval);
      document.title = originalTitle;
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const scoreVerdict = overallAuditScore != null
    ? overallAuditScore >= 80 ? 'pass' : overallAuditScore >= 50 ? 'warn' : 'fail'
    : null;

  return (
    <div
      style={{
        border: `1px solid ${FOREST_GREEN.faint}`,
        borderRadius: 8,
        background: WHITE,
        boxShadow: SHADOWS.md,
        padding: 16,
      }}
    >
      {/* Header */}
      <div className="flex items-center gap-2" style={{ marginBottom: 16 }}>
        <CheckCircle2 className="w-5 h-5" style={{ color: FOREST_GREEN.DEFAULT }} />
        <span style={{ fontWeight: 600, fontSize: 16, color: FOREST_GREEN.DEFAULT, flex: 1 }}>Plan Complete</span>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close completion card"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 24,
              height: 24,
              borderRadius: 4,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              color: MID_GRAY.DEFAULT,
            }}
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Artifact counts */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 16 }}>
        {Object.entries(artifactCounts).map(([key, count]) => (
          <div
            key={key}
            className="flex items-center justify-between"
            style={{ fontSize: 13, padding: '2px 0' }}
          >
            <span style={{ color: MID_GRAY.DEFAULT }}>{camelToTitle(key)}:</span>
            <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{count}</span>
          </div>
        ))}
      </div>

      {/* Stats */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          padding: '12px 0',
          borderTop: `1px solid ${BORDER.DEFAULT}`,
          marginBottom: 16,
        }}
      >
        {overallAuditScore != null && scoreVerdict && (
          <div className="flex items-center justify-between" style={{ fontSize: 13 }}>
            <span style={{ color: MID_GRAY.DEFAULT }}>Overall Audit Score:</span>
            <span style={{ fontWeight: 600, color: verdictColor(scoreVerdict) }}>
              {overallAuditScore}/100
            </span>
          </div>
        )}
        <div className="flex items-center justify-between" style={{ fontSize: 13 }}>
          <span style={{ color: MID_GRAY.DEFAULT }}>Total LLM Calls:</span>
          <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{totalLLMCalls}</span>
        </div>
        <div className="flex items-center justify-between" style={{ fontSize: 13 }}>
          <span style={{ color: MID_GRAY.DEFAULT }}>Execution Time:</span>
          <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{formatTime(executionTimeMs)}</span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={onStartNewPlan}
          aria-label="Start a new plan"
          className="flex items-center gap-1.5"
          style={{
            padding: '8px 20px',
            borderRadius: 6,
            border: 'none',
            background: FOREST_GREEN.DEFAULT,
            color: WHITE,
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          <Plus className="w-4 h-4" />
          Start New Plan
        </button>
        <button
          type="button"
          onClick={onViewDetails}
          aria-label="View execution details"
          className="flex items-center gap-1.5"
          style={{
            padding: '8px 20px',
            borderRadius: 6,
            border: `1px solid ${BORDER.DEFAULT}`,
            background: 'transparent',
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          <Eye className="w-4 h-4" />
          View Details
        </button>
      </div>

      {/* Undo All */}
      {onUndoAll && (
        <div style={{ marginTop: 12 }}>
          {showUndoConfirm ? (
            <div
              style={{
                padding: '8px 12px',
                borderRadius: 6,
                background: ERROR.bg,
                border: `1px solid ${ERROR.DEFAULT}20`,
                fontSize: 12,
              }}
            >
              <p style={{ margin: '0 0 8px', color: ERROR.DEFAULT }}>
                This will remove all {totalArtifacts} components, schemas, and workflows created by this plan. Continue?
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => { setShowUndoConfirm(false); onUndoAll(); }}
                  style={{
                    padding: '4px 12px',
                    borderRadius: 4,
                    border: 'none',
                    background: ERROR.DEFAULT,
                    color: WHITE,
                    cursor: 'pointer',
                    fontSize: 12,
                    fontWeight: 500,
                  }}
                >
                  Yes, undo all
                </button>
                <button
                  type="button"
                  onClick={() => setShowUndoConfirm(false)}
                  style={{
                    padding: '4px 12px',
                    borderRadius: 4,
                    border: `1px solid ${BORDER.DEFAULT}`,
                    background: 'transparent',
                    cursor: 'pointer',
                    fontSize: 12,
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowUndoConfirm(true)}
              className="flex items-center gap-1.5"
              style={{
                padding: '6px 12px',
                borderRadius: 6,
                border: 'none',
                background: 'transparent',
                color: ERROR.DEFAULT,
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Undo All
            </button>
          )}
        </div>
      )}
    </div>
  );
}
